<script setup>
import LockKeyIcon from '@iconify-vue/ph/lock-key';
import NotePencilIcon from '@iconify-vue/ph/note-pencil';
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { task, callback } = defineProps({
    task: {
        type: Object,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    name: task.name,
    description: task.description,
    internalComment: task.internalComment,
});

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    description: string().nullable(),
    internalComment: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.task.postTask', {
                task: task.id,
                quick: true,
            }),
            {
                name: form.name,
                description: form.description,
                internalComment: form.internalComment,
            },
        )
        .then(({ data }) => {
            isOpen.value = false;

            if (callback) {
                callback();
            }

            form.name = data.name;
            form.description = data.description;
            form.internalComment = data.internalComment;
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            console.error(error);
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="almost-full">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Mettre à jour </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <TabLayout
                    :tabs="{
                        task: {
                            title: 'Tâche',
                            contentSlotName: 'task',
                            forceMount: true,
                            icon: NotePencilIcon,
                        },
                        internalComment: {
                            title: 'Commentaire interne',
                            contentSlotName: 'internalComment',
                            forceMount: true,
                            icon: LockKeyIcon,
                        },
                    }"
                    :sync-url="false"
                    :unmount-on-hide="false"
                >
                    <template #task>
                        <FormColumn>
                            <FormContent>
                                <InputField
                                    v-model="form.name"
                                    label="Nom"
                                    placeholder="Nom"
                                    field-name="name"
                                    field-id="name"
                                    required
                                />
                            </FormContent>

                            <FormContent>
                                <EditorField
                                    v-model="form.description"
                                    label="Description"
                                    field-name="description"
                                    field-id="description"
                                    size="small"
                                />
                            </FormContent>
                        </FormColumn>
                    </template>

                    <template #internalComment>
                        <FormColumn>
                            <FormContent>
                                <EditorField
                                    v-model="form.internalComment"
                                    label="Commentaire interne"
                                    field-name="internalComment"
                                    field-id="internalComment"
                                    size="small"
                                />
                            </FormContent>
                        </FormColumn>
                    </template>
                </TabLayout>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
